import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';

interface LivisAreaLineChartProps {
    data: Array<{ name: string; value: number }>;
    height?: number;
    color?: string;
    yAxisTitle?: string;
    yAxisMin?: number;
    yAxisMax?: number;
    showTooltip?: boolean;
    noDataMessage?: string;
}

const LivisAreaLineChart: React.FC<LivisAreaLineChartProps> = ({
    data,
    height = 300,
    color = 'var(--livis-accent-color)',
    yAxisTitle = 'Drift Metrics',
    yAxisMin = 0,
    yAxisMax = 1.0,
    showTooltip = true,
    noDataMessage = 'No Data to Display'
}) => {
    const { t } = useTranslation();
    const hasData = data && data.length > 0;

    const categories = data.map((d) => d.name);
    const values = data.map((d) => d.value);

    if (!hasData) {
        return (
            <div
                style={{
                    width: '100%',
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <p style={{ color: 'var(--livis-color-primary-gray)' }}>
                    {t(noDataMessage)}
                </p>
            </div>
        );
    }

    const options: Highcharts.Options = {
        chart: {
            type: 'areaspline',
            backgroundColor: 'transparent',
            height: height,
        },
        title: { text: undefined },
        xAxis: {
            categories: categories,
            lineWidth: 0,
            tickWidth: 0,
            labels: {
                style: { fontSize: '10px', color: '#999' }
            }
        },
        yAxis: {
            title: {
                text: yAxisTitle,
                style: { fontSize: '10px', color: '#999' }
            },
            gridLineColor: '#E0E0E0',
            gridLineDashStyle: 'Dash',
            labels: {
                style: { color: '#999', fontSize: '10px' }
            },
            min: yAxisMin,
            max: yAxisMax
        },
        legend: { enabled: false },
        plotOptions: {
            areaspline: {
                lineWidth: 2.5,
                color: color,
                fillColor: {
                    linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                    stops: [
                        [0, color],
                        [1, 'rgba(33, 150, 243, 0.05)']
                    ]
                },
                marker: {
                    enabled: true,
                    radius: 5,
                    fillColor: color,
                    lineWidth: 2,
                    lineColor: '#FFFFFF'
                }
            }
        },
        series: [{
            type: 'areaspline',
            name: 'Drift',
            data: values
        }],
        credits: { enabled: false },
        tooltip: showTooltip ? {
            shared: true,
            backgroundColor: '#FFFFFF',
            borderColor: color,
            borderRadius: 6,
            style: { color: '#000' },
            formatter: function (this: any) {
                return `<b>${this.x}</b><br/>Value: ${this.y.toFixed(2)}`;
            }
        } : { enabled: false }
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default LivisAreaLineChart;